/* globals Cookies */
import * as Sentry from '@sentry/browser';

/**
 * Returns Authorization header if user token is set
 *
 * @return {object} Empty object if no token is set, else X-CSRFToken header object
 */
const getAuthorizationHeader = () => {
    const token = Cookies.get('csrftoken');
    if (!token) {
        Sentry.captureException(new Error('Missing csrftoken!'));
        return {};
    }
    return {
        'X-CSRFToken': token,
    };
};

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 * @return {object|undefined} Returns either the response, or throws an error
 */
const validateResponseStatus = (response) => {
    if (!response.ok) return new Error(response);
    return response;
};

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 * @return {object}          The parsed JSON from the request
 */
const parseResponseJSON = (response) => {
    if (response.status === 204 || response.status === 205) return null;
    return response.json();
};

/**
 * Parses the text response returned by a network request
 *
 * @param  {object} response Response stream
 * @return {object}          Parsed response
 */
const parseResponseText = (response) => response.text();

/**
 * Requests a URL, returning a promise
 * Catching an error should be handled by caller
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @return {object}           The response data
 */
export const fetchRequest = (url, options = {}) => {
    const authHeader = getAuthorizationHeader();
    const extendedOptions = {
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...authHeader,
        },
        method: options.method || 'GET',
        ...options,
    };

    return fetch(url, extendedOptions).then(validateResponseStatus).then(parseResponseJSON);
};

const apiFetchRequest = (path, options) => fetchRequest(`/api/${path}`, options);

/**
 * Fetch HTML from url provided
 *
 * @param {string} url
 * @param {object} options
 */
export const documentFetchRequest = (url, options = {}) => {
    const authHeader = getAuthorizationHeader();
    const extendedOptions = {
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            ...authHeader,
        },
        method: options.method || 'GET',
        ...options,
    };

    return fetch(url, extendedOptions).then(validateResponseStatus).then(parseResponseText);
};

export default apiFetchRequest;
